import {
	ApolloClient,
	InMemoryCache,
	createHttpLink,
	fromPromise,
	from
} from '@apollo/client';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { AUTH_TYPE } from 'aws-appsync-auth-link';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import rootStore from 'Stores/RootStore';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import mockAxios from './mocks';
import { GraphQLError } from 'app-types';
import { getAxiosErrorType } from 'Helpers/api';

const httpLink = createHttpLink({
	uri: process.env.REACT_APP_API_URL_GRAPHQL
});

// eslint-disable-next-line
const getHeaders = (headers: Record<string, string>) => {
	// eslint-disable-next-line
	const token = rootStore.authStore.authSession?.getIdToken() as any;
	return {
		headers: {
			...headers,
			Authorization: token ? `${token.jwtToken}` : ''
		}
	};
};

const authLink = setContext((_, { headers }) => getHeaders(headers));

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
	if (graphQLErrors) {
		for (const err of graphQLErrors) {
			// `GraphQLError` Interface isn't aligned with what is returned from BE
			switch (((err as unknown) as GraphQLError).errorType) {
				case 'UnauthorizedException':
					if (operation.getContext()._authRetry) {
						break;
					}
					operation.setContext({
						...operation.getContext(),
						_authRetry: true
					});
					const observable = fromPromise(
						rootStore.authStore
							.getAuthSession()
							.catch(() => rootStore.authStore.signOut())
					)
						.filter(Boolean)
						.flatMap(() => {
							const headers = operation.getContext().headers;
							// modify the operation context with a new token
							operation.setContext(getHeaders(headers));
							// retry the request, returning the new observable
							return forward(operation);
						});
					return observable;
			}
		}
	}
});

// @see https://github.com/awslabs/aws-mobile-appsync-sdk-js/issues/509
const subscriptionLink = createSubscriptionHandshakeLink(
	{
		url: process.env.REACT_APP_API_URL_GRAPHQL as string,
		region: process.env.REACT_APP_COGNITO_REGION as string,
		auth: {
			type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
			jwtToken: () =>
				rootStore.authStore.authSession?.getIdToken().getJwtToken() ||
				''
		}
	},
	httpLink
);

export const apolloClient = new ApolloClient({
	link: from([errorLink, authLink, subscriptionLink]),
	cache: new InMemoryCache({
		addTypename: false
	}),
	defaultOptions: {
		watchQuery: {
			fetchPolicy: 'no-cache'
		}
	}
});

// axios
export const axiosClient = axios.create({
	baseURL: process.env.REACT_APP_API_URL_REST as string
});

const addHeaders = (config: AxiosRequestConfig) => ({
	...config,
	...getHeaders({
		...config.headers
	})
});

axiosClient.interceptors.request.use(addHeaders);
axiosClient.interceptors.response.use(
	(res) => res,
	(error: AxiosError) => {
		const { isUnauthorizedError } = getAxiosErrorType(error);
		if (isUnauthorizedError) {
			return rootStore.authStore
				.getAuthSession()
				.then(() => axiosClient(error.config))
				.catch((error) => {
					const { isUnauthorizedError } = getAxiosErrorType(error);
					// signout if user if the exception is not authorized
					if (isUnauthorizedError) {
						rootStore.authStore.signOut();
					}

					return Promise.reject(error);
				});
		}

		return Promise.reject(error);
	}
);

mockAxios();

// directory axios — uses OAuth token (different auth from main axiosClient)
export const directoryAxiosClient = axios.create({
	baseURL: process.env.REACT_APP_API_URL_DIRECTORY as string
});

// Marks a request that has already been retried once after a 401, so the
// response interceptors don't refresh-and-retry it endlessly.
type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

const addDirectoryHeaders = (config: AxiosRequestConfig) => ({
	...config,
	headers: {
		...config.headers,
		'Content-Type': 'application/json',
		Authorization: `Bearer ${localStorage.getItem('oauth_token') ?? ''}`
	}
});

directoryAxiosClient.interceptors.request.use(addDirectoryHeaders);
directoryAxiosClient.interceptors.response.use(
	(res) => res,
	async (error: AxiosError) => {
		const { isUnauthorizedError } = getAxiosErrorType(error);
		const config = error.config as RetryableConfig | undefined;
		// Retry a 401 at most once — see the LMS interceptor below for why.
		if (isUnauthorizedError && config && !config._retry) {
			config._retry = true;
			try {
				await rootStore.authStore.fetchOAuthToken();
				return directoryAxiosClient(config);
			} catch {
				return Promise.reject(error);
			}
		}
		return Promise.reject(error);
	}
);

// LMS / Training Manager axios — OAuth token (same auth as directory).
// Base URL already includes `/api/v1`, so endpoint paths are relative to it.
export const lmsAxiosClient = axios.create({
	baseURL: process.env.REACT_APP_LMS_API_URL as string
});

// Unlike the directory client, LMS requests wait for a ready OAuth token
// before sending. On first load the dashboard/LMS fetches fire while the
// initial `gettoken` is still in flight; awaiting here means they never go out
// with an empty `Authorization: Bearer ` header (which the Training Manager API
// rejects with 401). Reuses the in-flight fetch, so parallel calls don't each
// trigger their own `gettoken`.
const addLmsHeaders = async (config: AxiosRequestConfig) => {
	const token = await rootStore.authStore.ensureOAuthToken();
	return {
		...config,
		headers: {
			...config.headers,
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`
		}
	};
};

lmsAxiosClient.interceptors.request.use(addLmsHeaders);
lmsAxiosClient.interceptors.response.use(
	(res) => res,
	async (error: AxiosError) => {
		const { isUnauthorizedError } = getAxiosErrorType(error);
		const config = error.config as RetryableConfig | undefined;
		// Retry a 401 at most once (refresh token, re-issue). Without this guard
		// an endpoint that keeps returning 401 loops forever:
		// request → 401 → gettoken → retry → 401 → gettoken → …
		if (isUnauthorizedError && config && !config._retry) {
			config._retry = true;
			try {
				await rootStore.authStore.fetchOAuthToken();
				return lmsAxiosClient(config);
			} catch {
				return Promise.reject(error);
			}
		}
		return Promise.reject(error);
	}
);
